import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import CurrentUserContext from '../CurrentUser';

import PermissionMatrixContext, { type CellDraft, type EmployeeListItem, cellKey } from './Context';

import {
  CompanyEmploymentsListDocument,
  AccessRegistryDocument,
  GetEmploymentAccessDocument,
  type GetEmploymentAccessQuery,
  GetRolesDocument,
  SetEmploymentGrantsDocument,
  AssignRoleDocument,
  UnassignRoleDocument,
  AccessProjectsPickerDocument,
} from '@/graphql/generated/graphql';
import { type RawDate } from '@/types/globals';
import { displayNotification } from '@/utils/notifications';

interface PermissionMatrixProviderProps {
  children: React.ReactNode;
}

function draftsEqual(a: Map<string, CellDraft>, b: Map<string, CellDraft>) {
  if (a.size !== b.size) return false;
  for (const [key, cell] of a) {
    const other = b.get(key);
    if (!other) return false;
    const idsA = cell.projectIds === null ? null : [...cell.projectIds].sort().join(',');
    const idsB = other.projectIds === null ? null : [...other.projectIds].sort().join(',');
    if (idsA !== idsB || cell.validTill !== other.validTill) return false;
  }
  return true;
}

function buildDraft(access: GetEmploymentAccessQuery['getEmploymentAccess']): Map<string, CellDraft> {
  const draft = new Map<string, CellDraft>();
  if (!access) return draft;

  for (const grant of access.directGrants) {
    if (grant.expired) continue;

    const key = cellKey(grant.action, grant.entity);
    const existing = draft.get(key);
    // validTill is an opaque RawDate over the wire; the draft holds ISO strings.
    const validTill = (grant.validTill as unknown as string) ?? null;

    if (grant.projectId == null) {
      draft.set(key, { projectIds: null, validTill });
    } else if (existing && existing.projectIds !== null) {
      existing.projectIds.push(String(grant.projectId));
    } else if (!existing) {
      draft.set(key, { projectIds: [String(grant.projectId)], validTill });
    }
    // a company-wide row already covers any scoped rows for the same pair
  }

  return draft;
}

const PermissionMatrixProvider = ({ children }: PermissionMatrixProviderProps) => {
  const { currentUser } = useContext(CurrentUserContext);
  const [selectedEmploymentId, setSelectedEmploymentId] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<Map<string, CellDraft>>(new Map());
  const [draft, setDraft] = useState<Map<string, CellDraft>>(new Map());
  const [saving, setSaving] = useState(false);

  const { data: employeesData, loading: employeeListLoading } = useQuery(CompanyEmploymentsListDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      includeOwner: false,
      includeSelf: false,
    },
  });

  const { data: registryData, loading: registryLoading } = useQuery(AccessRegistryDocument, {
    fetchPolicy: 'cache-first',
  });

  const {
    data: rolesData,
    loading: rolesLoading,
    refetch: refetchRoles,
  } = useQuery(GetRolesDocument, { fetchPolicy: 'cache-and-network' });

  const companyId = currentUser?.defaultCompany?.id;
  const { data: projectsData } = useQuery(AccessProjectsPickerDocument, {
    skip: !companyId,
    variables: { companyId: companyId ?? '' },
    fetchPolicy: 'cache-first',
  });

  const [fetchAccess, { data: accessData, loading: selectedPermissionsLoading, refetch: refetchAccess }] = useLazyQuery(
    GetEmploymentAccessDocument,
    { fetchPolicy: 'network-only' },
  );

  const [setEmploymentGrants] = useMutation(SetEmploymentGrantsDocument);
  const [assignRole] = useMutation(AssignRoleDocument);
  const [unassignRole] = useMutation(UnassignRoleDocument);

  const employeeList: EmployeeListItem[] = useMemo(() => {
    const list = (employeesData?.companyEmployments ?? []) as EmployeeListItem[];
    return list.map((e) => ({ id: String(e.id), fullName: e.fullName, imageUrl: e.imageUrl }));
  }, [employeesData]);

  const selectedEmployee = useMemo(() => {
    if (!selectedEmploymentId) return null;
    return employeeList.find((e) => e.id === selectedEmploymentId) ?? null;
  }, [employeeList, selectedEmploymentId]);

  const selectedAccess = accessData?.getEmploymentAccess ?? null;

  useEffect(() => {
    if (!selectedEmploymentId) return;
    setBaseline(new Map());
    setDraft(new Map());
    fetchAccess({ variables: { employmentId: selectedEmploymentId } });
  }, [selectedEmploymentId, fetchAccess]);

  useEffect(() => {
    if (!selectedAccess) return;
    const built = buildDraft(selectedAccess);
    setBaseline(built);
    setDraft(buildDraft(selectedAccess));
  }, [selectedAccess]);

  const roleGrantSources = useMemo(() => {
    const sources = new Map<string, string[]>();
    for (const role of selectedAccess?.roles ?? []) {
      for (const grant of role.grants) {
        const key = cellKey(grant.action, grant.entity);
        sources.set(key, [...(sources.get(key) ?? []), role.name]);
      }
    }
    return sources;
  }, [selectedAccess]);

  const selectEmployment = useCallback((employmentId: string) => {
    setSelectedEmploymentId(employmentId);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedEmploymentId(null);
    setBaseline(new Map());
    setDraft(new Map());
  }, []);

  const updateDraft = useCallback((updater: (next: Map<string, CellDraft>) => void) => {
    setDraft((prev) => {
      const next = new Map(prev);
      updater(next);
      return next;
    });
  }, []);

  const toggleCell = useCallback(
    (action: string, entity: string) => {
      updateDraft((next) => {
        const key = cellKey(action, entity);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, { projectIds: null, validTill: null });
        }
      });
    },
    [updateDraft],
  );

  const setColumn = useCallback(
    (action: string, entityKeys: string[], checked: boolean) => {
      updateDraft((next) => {
        entityKeys.forEach((entity) => {
          const key = cellKey(action, entity);
          if (checked) {
            if (!next.has(key)) next.set(key, { projectIds: null, validTill: null });
          } else {
            next.delete(key);
          }
        });
      });
    },
    [updateDraft],
  );

  const setCellScope = useCallback(
    (action: string, entity: string, projectIds: string[] | null) => {
      updateDraft((next) => {
        const key = cellKey(action, entity);
        if (projectIds !== null && projectIds.length === 0) {
          next.delete(key);
          return;
        }
        const current = next.get(key);
        next.set(key, { projectIds, validTill: current?.validTill ?? null });
      });
    },
    [updateDraft],
  );

  const setCellExpiry = useCallback(
    (action: string, entity: string, validTill: string | null) => {
      updateDraft((next) => {
        const key = cellKey(action, entity);
        const current = next.get(key);
        if (!current) return;
        next.set(key, { ...current, validTill });
      });
    },
    [updateDraft],
  );

  const resetDraft = useCallback(() => {
    setDraft(
      new Map(
        Array.from(baseline, ([k, v]) => [k, { ...v, projectIds: v.projectIds === null ? null : [...v.projectIds] }]),
      ),
    );
  }, [baseline]);

  const isDirty = !draftsEqual(baseline, draft);

  const saveDraft = useCallback(async () => {
    if (!selectedEmploymentId || !isDirty) return;

    const grants = Array.from(draft.entries()).flatMap(([key, cell]) => {
      const [action, entity] = key.split(':');
      // ISO strings go back out as the schema's opaque date scalar.
      const validTill = cell.validTill as unknown as RawDate | null;

      if (cell.projectIds === null) {
        return [{ action, entity, validTill }];
      }
      return cell.projectIds.map((projectId) => ({ action, entity, projectId, validTill }));
    });

    setSaving(true);
    try {
      await setEmploymentGrants({ variables: { employmentId: selectedEmploymentId, grants } });
      await refetchAccess({ employmentId: selectedEmploymentId });
      displayNotification({ type: 'success', text: 'Permissions updated.' });
    } catch {
      displayNotification({ type: 'error', text: 'Permissions could not be saved. Please try again.' });
    } finally {
      setSaving(false);
    }
  }, [selectedEmploymentId, isDirty, draft, setEmploymentGrants, refetchAccess]);

  const assignRoleTo = useCallback(
    async (employmentId: string, roleId: string) => {
      await assignRole({ variables: { employmentId, roleId } });
      await Promise.all([refetchRoles(), refetchAccess({ employmentId })]);
    },
    [assignRole, refetchRoles, refetchAccess],
  );

  const unassignRoleFrom = useCallback(
    async (employmentId: string, roleId: string) => {
      await unassignRole({ variables: { employmentId, roleId } });
      await Promise.all([refetchRoles(), refetchAccess({ employmentId })]);
    },
    [unassignRole, refetchRoles, refetchAccess],
  );

  const projectOptions = useMemo(() => {
    const edges = projectsData?.getProjects?.edges ?? [];
    return edges.flatMap((edge) =>
      edge?.node ? [{ id: String(edge.node.id), title: edge.node.title, code: edge.node.code }] : [],
    );
  }, [projectsData]);

  const contextValue = {
    employeeList,
    employeeListLoading,

    registry: registryData?.accessRegistry ?? null,
    registryLoading,

    selectedEmploymentId,
    selectedEmployee,
    selectEmployment,
    clearSelection,

    selectedAccess,
    selectedPermissionsLoading,
    roleGrantSources,

    draft,
    toggleCell,
    setColumn,
    setCellScope,
    setCellExpiry,
    resetDraft,
    saveDraft,
    saving,
    isDirty,

    companyRoles: rolesData?.getRoles ?? [],
    rolesLoading,
    assignRoleTo,
    unassignRoleFrom,
    refetchRoles: () => {
      refetchRoles();
    },

    projectOptions,
  };

  return <PermissionMatrixContext.Provider value={contextValue}>{children}</PermissionMatrixContext.Provider>;
};

export default PermissionMatrixProvider;
